<script setup lang="ts">
import { ref, watch } from 'vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { sdk } from '@/graphql/client'
import { relayIdToInt } from '@/lib/relayId'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { ticketEditSchema, type TicketEditInput } from './schemas'
import BBtn from '@lib/components/base/BBtn.vue'
import BInput from '@lib/components/base/BInput.vue'
import BDateTimeInput from '@lib/components/base/BDateTimeInput.vue'
import Checkbox from 'primevue/checkbox'
import { Trash2, ShieldCheck, ClipboardPlus } from 'lucide-vue-next'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import { useClipboardStore } from '@/stores/clipboard'

interface TicketLike {
    id: string | number
    code: string
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    phone?: string | null
    used?: boolean | null
    usedAt?: string | null
    boughtAt?: string | null
    validUntil?: string | null
    ticketNumber?: string | null
}

const props = defineProps<{
    ticket: TicketLike
}>()

const emit = defineEmits<{
    (e: 'saved'): void
    (e: 'deleted'): void
    (e: 'cancel'): void
}>()

const { t } = useI18n()
const toast = useToast()
const { confirm } = useConfirm()
const router = useRouter()
const clipboard = useClipboardStore()

function initialValues(): TicketEditInput {
    return {
        code: props.ticket.code ?? '',
        email: props.ticket.email ?? '',
        firstName: props.ticket.firstName ?? '',
        lastName: props.ticket.lastName ?? '',
        phone: props.ticket.phone ?? '',
        used: Boolean(props.ticket.used),
        usedAt: props.ticket.usedAt ?? '',
        boughtAt: props.ticket.boughtAt ?? '',
        validUntil: props.ticket.validUntil ?? '',
        ticketNumber: props.ticket.ticketNumber ?? ''
    }
}

const { handleSubmit, defineField, errors, resetForm, meta } = useForm<TicketEditInput>({
    validationSchema: toTypedSchema(ticketEditSchema),
    initialValues: initialValues()
})

const [code] = defineField('code')
const [email] = defineField('email')
const [firstName] = defineField('firstName')
const [lastName] = defineField('lastName')
const [phone] = defineField('phone')
const [used] = defineField('used')
const [usedAt] = defineField('usedAt')
const [boughtAt] = defineField('boughtAt')
const [validUntil] = defineField('validUntil')
const [ticketNumber] = defineField('ticketNumber')

const submitting = ref(false)
const deleting = ref(false)

function ticketIdAsInt(): number | null {
    return relayIdToInt(props.ticket.id)
}

const onSubmit = handleSubmit(async values => {
    const id = ticketIdAsInt()
    if (id === null) {
        toast.error(t('common.errors.generic'))
        return
    }
    submitting.value = true
    try {
        const r = await sdk.AdminUpdateTicket({
            id,
            code: values.code,
            email: values.email || undefined,
            firstName: values.firstName || undefined,
            lastName: values.lastName || undefined,
            phone: values.phone || undefined,
            used: values.used,
            usedAt: values.usedAt || undefined,
            boughtAt: values.boughtAt || undefined,
            validUntil: values.validUntil || undefined,
            ticketNumber: values.ticketNumber || undefined
        })
        if (r.admin?.ticket?.update?.ok) {
            toast.success(t('tickets.toasts.saved'))
            resetForm({ values })
            emit('saved')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        submitting.value = false
    }
})

async function onDelete() {
    const id = ticketIdAsInt()
    if (id === null) {
        toast.error(t('common.errors.generic'))
        return
    }
    const ok = await confirm({
        title: t('tickets.confirm.deleteTitle'),
        message: t('tickets.confirm.deleteMessage', { code: props.ticket.code }),
        severity: 'danger'
    })
    if (!ok) return
    deleting.value = true
    try {
        const r = await sdk.AdminDeleteTicket({ id })
        if (r.admin?.ticket?.delete?.ok) {
            toast.success(t('tickets.toasts.deleted'))
            emit('deleted')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        deleting.value = false
    }
}

async function onCancel() {
    if (meta.value.dirty) {
        const ok = await confirm({
            title: t('tickets.edit.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    emit('cancel')
}

async function onVerify() {
    if (meta.value.dirty) {
        const ok = await confirm({
            title: t('tickets.edit.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    router.push({ path: '/tickets/verify', query: { code: props.ticket.code } })
}

function onAddToClipboard() {
    clipboard.addTicket(props.ticket.code)
    toast.success(t('tickets.toasts.addedToClipboard'))
}

// Re-init when ticket changes (different row expanded)
watch(
    () => props.ticket.id,
    () => {
        resetForm({ values: initialValues() })
    }
)

defineExpose({ isDirty: () => meta.value.dirty })
</script>

<template>
    <form class="space-y-4" @submit.prevent="onSubmit">
        <div class="flex flex-col md:flex-row gap-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-w-0">
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{ t('tickets.list.columns.code') }}</span>
                <BInput v-model="code" :invalid="!!errors.code" />
                <span v-if="errors.code" class="text-xs text-red mt-1 block">{{ errors.code }}</span>
            </label>
            <label for="ticket-used" class="flex items-center gap-2 mt-5 cursor-pointer">
                <Checkbox
                    input-id="ticket-used"
                    :model-value="used"
                    :binary="true"
                    @update:model-value="v => (used = Boolean(v))"
                />
                <span class="text-xs font-semibold text-gray-700">{{ t('tickets.usedLabel.used') }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.firstName')
                }}</span>
                <BInput v-model="firstName" :invalid="!!errors.firstName" />
                <span v-if="errors.firstName" class="text-xs text-red mt-1 block">{{ errors.firstName }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.lastName')
                }}</span>
                <BInput v-model="lastName" :invalid="!!errors.lastName" />
                <span v-if="errors.lastName" class="text-xs text-red mt-1 block">{{ errors.lastName }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.email')
                }}</span>
                <BInput v-model="email" type="email" :invalid="!!errors.email" />
                <span v-if="errors.email" class="text-xs text-red mt-1 block">{{ errors.email }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.phone')
                }}</span>
                <BInput v-model="phone" :invalid="!!errors.phone" />
                <span v-if="errors.phone" class="text-xs text-red mt-1 block">{{ errors.phone }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.ticketNumber')
                }}</span>
                <BInput v-model="ticketNumber" :invalid="!!errors.ticketNumber" />
                <span v-if="errors.ticketNumber" class="text-xs text-red mt-1 block">{{ errors.ticketNumber }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.validUntil')
                }}</span>
                <BDateTimeInput v-model="validUntil" :invalid="!!errors.validUntil" />
                <span v-if="errors.validUntil" class="text-xs text-red mt-1 block">{{ errors.validUntil }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.boughtAt')
                }}</span>
                <BDateTimeInput v-model="boughtAt" :invalid="!!errors.boughtAt" />
                <span v-if="errors.boughtAt" class="text-xs text-red mt-1 block">{{ errors.boughtAt }}</span>
            </label>
            <label class="block">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">{{
                    t('tickets.list.columns.usedAt')
                }}</span>
                <BDateTimeInput v-model="usedAt" :invalid="!!errors.usedAt" />
                <span v-if="errors.usedAt" class="text-xs text-red mt-1 block">{{ errors.usedAt }}</span>
            </label>
            </div>

            <!-- Third column: ticket QR + code + add-to-clipboard. -->
            <div class="md:w-48 shrink-0 flex flex-col items-center gap-2 border-t md:border-t-0 md:border-l border-gray-200 pt-4 md:pt-0 md:pl-4">
                <QrPreview :code="props.ticket.code" label="" :size="150" />
                <div class="text-xs font-mono text-center break-all">{{ props.ticket.code }}</div>
                <BBtn severity="secondary" size="small" type="button" @click="onAddToClipboard">
                    <ClipboardPlus class="w-4 h-4" />
                    <span>{{ t('tickets.detail.addToClipboard') }}</span>
                </BBtn>
            </div>
        </div>

        <div class="flex items-center justify-between gap-2 pt-3 border-t border-gray-200">
            <div class="flex items-center gap-2">
                <BBtn severity="danger" :loading="deleting" type="button" @click="onDelete">
                    <Trash2 class="w-4 h-4" />
                    <span>{{ t('common.actions.delete') }}</span>
                </BBtn>
                <BBtn
                    severity="secondary"
                    type="button"
                    @click="onVerify"
                >
                    <ShieldCheck class="w-4 h-4" />
                    <span>{{ t('tickets.verify.verify') }}</span>
                </BBtn>
            </div>
            <div class="flex items-center gap-2">
                <BBtn severity="secondary" type="button" :label="t('common.actions.cancel')" @click="onCancel" />
                <BBtn type="submit" :loading="submitting" :label="t('common.actions.saveChanges')" />
            </div>
        </div>
    </form>
</template>
